import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CircleHelp, Search, Sparkles } from "lucide-react";
import { GLOBAL_IMAGES } from "../assets/asset";
import { FILTER_OPTIONS } from "./tutorInfoData";
import {
  getTeacherDisplayName,
  getTeacherIdentifier,
  getTeacherPhotoValue,
  normalizeTeacherCollection,
  teacherApi,
} from "../features/teacher/teacherApi";
import {
  PARENT_BOOKING_HUB_PATH,
  persistParentPostAuthRedirect,
} from "../features/parentFlow/parentFlow";
import { logout } from "../features/auth/user.reducer";
import { formatTeacherYearLabels } from "../utils/teacherYears";
import { normalizeAudPriceForDisplay } from "../utils/audCurrency";
import { resolveApiImageUrl } from "../utils/imageUrl";
import { getStaticTutorDescription } from "./tutorDescriptions";

const PUBLIC_FILTER_OPTIONS = FILTER_OPTIONS.filter(
  (filter) => !["primary", "senior"].includes(filter.id)
);

const STATIC_RESPONSE_SPEEDS = [
  "Replies within 1 hour",
  "Replies same day",
  "Replies within 3 hours",
  "Replies by tomorrow",
];

const getTeacherYears = (teacher = {}) => {
  const yearSource =
    teacher?.year_labels?.length || typeof teacher?.year_labels === "string"
      ? teacher.year_labels
      : teacher?.year;

  return formatTeacherYearLabels(yearSource);
};

const getInitials = (name = "Teacher") =>
  String(name || "Teacher")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "T";

const formatPriceLabel = (price) => {
  if (typeof price !== "number" || Number.isNaN(price)) {
    return "Price on request";
  }

  const formatter = new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(price) ? 0 : 2,
  });

  return `$${formatter.format(price)} / h`;
};

const buildTutorCardModel = (teacher, index) => {
  const name = getTeacherDisplayName(teacher);
  const years = getTeacherYears(teacher);
  const rawPrice = teacher?.price_per_session ?? teacher?.price;
  const price = normalizeAudPriceForDisplay(rawPrice);
  const photo = resolveApiImageUrl(getTeacherPhotoValue(teacher));

  return {
    id: getTeacherIdentifier(teacher) || `teacher-${index}`,
    routeId: getTeacherIdentifier(teacher),
    name,
    years,
    initials: getInitials(name),
    image: photo,
    price,
    responseTime: STATIC_RESPONSE_SPEEDS[index % STATIC_RESPONSE_SPEEDS.length],
    description: getStaticTutorDescription(index),
  };
};

const matchesSelectedFilter = (tutor, selectedFilter) => {
  switch (selectedFilter) {
    case "online":
      return true;
    case "budget":
      return typeof tutor.price === "number" ? tutor.price < 20 : false;
    case "fast":
      return tutor.responseTime.toLowerCase().includes("1 hour");
    default:
      return true;
  }
};

export default function TutorInfoPage() {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.user);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [showAllTutors, setShowAllTutors] = useState(false);

  const teacherListQuery = useQuery({
    queryKey: ["public-tutor-info"],
    queryFn: () => teacherApi.getAllTeachers(),
    retry: false,
  });

  const tutors = useMemo(
    () =>
      normalizeTeacherCollection(teacherListQuery.data).map((teacher, index) =>
        buildTutorCardModel(teacher, index)
      ),
    [teacherListQuery.data]
  );

  const filteredTutors = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return tutors.filter((tutor) => {
      const searchableContent = [
        tutor.name,
        tutor.description,
        tutor.responseTime,
        tutor.years.join(" "),
        "online",
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableContent.includes(normalizedSearch);

      return matchesSearch && matchesSelectedFilter(tutor, selectedFilter);
    });
  }, [searchTerm, selectedFilter, tutors]);

  useEffect(() => {
    setShowAllTutors(false);
  }, [searchTerm, selectedFilter]);

  const visibleTutors = useMemo(
    () => (showAllTutors ? filteredTutors : filteredTutors.slice(0, 9)),
    [filteredTutors, showAllTutors]
  );

  const handleParentBookingHubClick = () => {
    persistParentPostAuthRedirect(PARENT_BOOKING_HUB_PATH);
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f3ea] font-mali text-[#1b1b1b]">
      <div className="mx-auto max-w-7xl px-4 pb-12 pt-6 sm:px-6 lg:px-8">
        <header className="border-b border-[#ddd3c6] pb-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between xl:flex-1">
              <Link className="flex items-center gap-3" to="/tutorinfo">
                <img
                  src={GLOBAL_IMAGES.dingo}
                  alt="MathDingo"
                  className="h-12 w-12 rounded-full bg-white p-2"
                />
                <img
                  src={GLOBAL_IMAGES.dingoLogoText}
                  alt="MathDingo"
                  className="h-8 w-auto object-contain sm:h-9"
                />
              </Link>

              <div className="flex w-full max-w-[460px] items-center rounded-full border border-[#d8cfbf] bg-white px-2 py-2 sm:px-3">
                <label htmlFor="tutor-info-search" className="sr-only">
                  Search tutors
                </label>
                <input
                  id="tutor-info-search"
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search tutors"
                  className="h-10 flex-1 rounded-full border-0 bg-transparent px-3 text-sm font-medium text-[#1f2740] outline-none placeholder:text-[#75809a] sm:text-base"
                />
                <button
                  type="button"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-[#08059B] text-white transition hover:bg-[#01044F]"
                >
                  <Search size={18} />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 xl:justify-end">
              <Link
                to="/contact"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d8cfbf] bg-white text-[#0B2B66]"
                aria-label="Contact support"
              >
                <CircleHelp size={18} />
              </Link>
              <Link
                to="/teachersignup"
                className="rounded-full border border-[#d8cfbf] bg-white px-5 py-2.5 text-sm font-bold text-[#0B2B66] sm:text-base"
              >
                Become a Tutor
              </Link>
              {token ? (
                <>
                  <Link
                    to="/parent/classBooking/myBookings"
                    className="rounded-full bg-[#08059B] px-5 py-2.5 text-sm font-bold text-white sm:text-base"
                  >
                    My Bookings
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-full border border-[#d8cfbf] bg-white px-5 py-2.5 text-sm font-bold text-[#0B2B66] transition hover:border-[#08059B] hover:text-[#08059B] sm:text-base"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/parent/login"
                  className="rounded-full bg-[#08059B] px-5 py-2.5 text-sm font-bold text-white sm:text-base"
                >
                  Parent Log In
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className="pt-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#9d5229]">
              <Sparkles size={16} />
              For parents looking for extra maths support
            </span>

            <h1 className="mt-6 text-3xl font-bold leading-tight text-[#0B2B66] sm:text-4xl lg:text-5xl">
              Maths tutors in Australia
            </h1>

            <p className="mt-4 max-w-2xl text-base leading-7 text-[#334155] sm:text-lg">
              Browse experienced tutors, compare pricing, and choose a learning
              style that fits your child&apos;s confidence, year level and weekly
              schedule.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {PUBLIC_FILTER_OPTIONS.map((filter) => {
              const isActive = selectedFilter === filter.id;

              return (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setSelectedFilter(filter.id)}
                  className={`rounded-full border px-5 py-2.5 text-sm font-bold transition sm:text-base ${
                    isActive
                      ? "border-[#08059B] bg-[#08059B] text-white"
                      : "border-[#d8cfbf] bg-transparent text-[#0B2B66] hover:border-[#08059B]"
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-2xl font-bold text-[#0B2B66]">
                {teacherListQuery.isLoading ? "Loading tutors..." : `${filteredTutors.length} available tutors`}
              </p>
              <p className="mt-2 text-sm text-[#4b5563]">
                Every profile includes response speed, year levels and a clear
                starting rate for parents.
              </p>
            </div>

            {token ? (
              <Link
                to={PARENT_BOOKING_HUB_PATH}
                onClick={handleParentBookingHubClick}
                className="inline-flex items-center gap-2 self-start rounded-full bg-[#08059B] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#01044F] sm:text-base"
              >
                Continue to booking
                <ArrowRight size={18} />
              </Link>
            ) : (
              <Link
                to="/parent/signup"
                className="inline-flex items-center gap-2 self-start rounded-full bg-[#08059B] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#01044F] sm:text-base"
              >
                Create parent account
                <ArrowRight size={18} />
              </Link>
            )}
          </div>

          {teacherListQuery.isLoading ? (
            <div className="mt-10 rounded-[34px] border border-[#ddd3c6] bg-white px-6 py-12 text-center text-base font-medium text-[#475569] shadow-sm">
              Loading tutor profiles...
            </div>
          ) : teacherListQuery.isError ? (
            <div className="mt-10 rounded-[34px] border border-[#f1c8bf] bg-[#fff7f5] px-6 py-12 text-center text-base font-medium text-[#a04432] shadow-sm">
              {teacherListQuery.error?.message ||
                "We couldn't load tutors right now. Please try again shortly."}
            </div>
          ) : !filteredTutors.length ? (
            <div className="mt-10 rounded-[34px] border border-[#ddd3c6] bg-white px-6 py-12 text-center text-base font-medium text-[#475569] shadow-sm">
              No tutors matched your current search or filters.
            </div>
          ) : (
            <>
              <section className="mt-10 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
                {visibleTutors.map((tutor) => (
                  <article
                    key={tutor.id}
                    className="flex h-full flex-col rounded-[34px] border border-[#e8dece] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.06)]"
                  >
                    <div className="flex items-start gap-4">
                      {tutor.image ? (
                        <img
                          src={tutor.image}
                          alt={tutor.name}
                          className="h-24 w-24 rounded-[28px] object-cover"
                        />
                      ) : (
                        <div className="flex h-24 w-24 items-center justify-center rounded-[28px] bg-gradient-to-br from-[#ff8a5b] to-[#ff5a5f] text-3xl font-bold text-white">
                          {tutor.initials}
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <h2 className="text-2xl font-bold text-[#0B2B66]">
                          {tutor.name}
                        </h2>
                        <p className="mt-2 inline-flex rounded-full bg-[#eef2ff] px-3 py-1 text-sm font-bold text-[#3949ab]">
                          Online
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-1 flex-col">
                      <p className="text-[16px] leading-8 text-[#374151]">
                        {tutor.description}
                      </p>

                      <div className="mt-auto pt-4 pb-2">
                        <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#64748b]">
                          Year Taught
                        </p>

                        <div className="-ml-2 mt-3 flex flex-wrap items-center gap-2">
                          {tutor.years.length ? (
                            tutor.years.map((year) => (
                              <span
                                key={`${tutor.id}-${year}`}
                                className="rounded-full border border-[#ddd3c6] bg-white px-4 py-2 text-sm font-medium text-[#475569]"
                              >
                                {year}
                              </span>
                            ))
                          ) : (
                            <span className="rounded-full border border-dashed border-[#ddd3c6] bg-[#faf7f2] px-4 py-2 text-sm font-medium text-[#64748b]">
                              Year levels coming soon
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto flex items-end justify-between gap-4 border-t border-[#efe7da] pt-5">
                      <div>
                        <p className="text-3xl font-bold text-[#111827]">
                          {formatPriceLabel(tutor.price)}
                        </p>
                        <p className="mt-1 text-sm font-medium text-[#ff6b61]">
                          {tutor.responseTime}
                        </p>
                      </div>

                      {tutor.routeId ? (
                        <Link
                          to={`/tutorinfo/${tutor.routeId}`}
                          className="inline-flex items-center gap-2 rounded-full bg-[#08059B] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#01044F]"
                        >
                          Profile
                          <ArrowRight size={17} />
                        </Link>
                      ) : (
                        <span className="inline-flex rounded-full border border-[#ddd3c6] px-5 py-3 text-sm font-bold text-[#64748b]">
                          Profile unavailable
                        </span>
                      )}
                    </div>
                  </article>
                ))}
              </section>

              {!showAllTutors && filteredTutors.length > 9 ? (
                <div className="mt-12 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setShowAllTutors(true)}
                    className="w-full max-w-md rounded-full bg-[#08059B] px-6 py-3 text-center text-base font-bold text-white transition hover:bg-[#01044F] sm:text-lg"
                  >
                    Show more tutors
                  </button>
                </div>
              ) : null}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
